import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { usersApi, Media } from '../../../api/usersApi';
import { getAvatarUrl } from '../../../utils/avatarUtils';
import { wallPostsApi, WallPost } from '../../../api/wallPostsApi';
import { Post } from '../../profile';
import CreatePost from '../../profile/ui/CreatePost';
import PostsFeed from '../../profile/ui/PostsFeed';
import EditPostModal from '../../profile/ui/EditPostModal';
import SkeletonPost from '../../profile/ui/SkeletonPost';
import { BottomSheet } from '../../../shared/ui';
import FavoritesGridOverlay from './FavoritesGridOverlay';
import styles from './RyaskovProCard.module.css';

export interface RyaskovProCardProps {
  /** Управление из главной шапки: показывать overlay избранного */
  showFavorites?: boolean;
  /** Управление из главной шапки: показывать окно создания поста */
  showCreatePost?: boolean;
  onCloseFavorites?: () => void;
  onCloseCreatePost?: () => void;
  /** Клик по объекту в шапке поста — переход на страницу объекта */
  onSiteClick?: (siteId: number) => void;
  /** Клик по автору поста — открывает профиль как полноэкранный оверлей */
  onUserClick?: (userId: number) => void;
}

const RyaskovProCard: React.FC<RyaskovProCardProps> = ({
  showFavorites: showFavoritesProp,
  showCreatePost: showCreatePostProp,
  onCloseFavorites,
  onCloseCreatePost,
  onSiteClick,
  onUserClick: onUserClickProp,
}) => {
  const { user } = useAuth();
  const [avatar, setAvatar] = useState<Media | null>(null);
  const [posts, setPosts] = useState<WallPost[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [showCreatePostLocal, setShowCreatePostLocal] = useState(false);
  const [showFavoritesLocal, setShowFavoritesLocal] = useState(false);
  const [showFavoriteFeed, setShowFavoriteFeed] = useState(false);
  const [favoriteFeedInitialPostId, setFavoriteFeedInitialPostId] = useState<number | null>(null);
  const [editingPost, setEditingPost] = useState<WallPost | null>(null);

  // Управляемый или локальный режим для overlay'ев
  const showFavorites = showFavoritesProp ?? showFavoritesLocal;
  const showCreatePost = showCreatePostProp ?? showCreatePostLocal;
  const closeFavorites = useCallback(() => {
    if (onCloseFavorites) onCloseFavorites();
    else setShowFavoritesLocal(false);
  }, [onCloseFavorites]);
  const closeCreatePost = useCallback(() => {
    if (onCloseCreatePost) onCloseCreatePost();
    else setShowCreatePostLocal(false);
  }, [onCloseCreatePost]);

  // Загружаем аватарку при монтировании
  useEffect(() => {
    if (user?.id) {
      loadAvatar();
      loadPosts();
    }
  }, [user?.id]);

  const loadAvatar = async () => {
    if (!user?.id) return;
    try {
      const avatarData = await usersApi.getAvatar(user.id);
      setAvatar(avatarData);
    } catch (error) {
      console.error('Ошибка загрузки аватарки:', error);
      setAvatar(null);
    }
  };

  const loadPosts = async () => {
    setIsLoadingPosts(true);
    try {
      const allPosts = await wallPostsApi.getAllPosts(100, 0);
      setPosts(allPosts);
    } catch (error) {
      console.error('Ошибка загрузки постов:', error);
      setPosts([]);
    } finally {
      setIsLoadingPosts(false);
    }
  };

  // Сортируем посты по рейтингу (лайки и дата публикации)
  // Используем формулу похожую на Reddit: рейтинг = лайки / (время в часах + 2)^1.5
  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => {
      const now = new Date().getTime();
      const postDateA = new Date(a.createdAt).getTime();
      const postDateB = new Date(b.createdAt).getTime();
      const hoursSincePostA = Math.max(1, (now - postDateA) / (1000 * 60 * 60)); // минимум 1 час
      const hoursSincePostB = Math.max(1, (now - postDateB) / (1000 * 60 * 60)); // минимум 1 час
      const gravity = 1.5; // фактор затухания
      const ratingA = a.likes / Math.pow(hoursSincePostA + 2, gravity);
      const ratingB = b.likes / Math.pow(hoursSincePostB + 2, gravity);
      return ratingB - ratingA; // сортируем по убыванию рейтинга
    });
  }, [posts]);

  const favoritePosts = useMemo(() => {
    return posts.filter(post => post.isLiked);
  }, [posts]);

  const handleFavoriteGridClick = useCallback((post: WallPost, imageIndex: number) => {
    closeFavorites();
    setFavoriteFeedInitialPostId(post.id);
    setShowFavoriteFeed(true);
  }, [closeFavorites]);

  const handleUserClick = (userId: number) => {
    onUserClickProp?.(userId);
  };

  const handleLike = async (postId: number) => {
    try {
      // Обновляем состояние оптимистично
      setPosts(posts.map(post => {
        if (post.id === postId) {
          const wasLiked = post.isLiked;
          return {
            ...post,
            isLiked: !wasLiked,
            likes: wasLiked ? post.likes - 1 : post.likes + 1
          };
        }
        return post;
      }));

      // Отправляем запрос на сервер
      await wallPostsApi.toggleLike(postId);
    } catch (error) {
      console.error('Ошибка при лайке поста:', error);
      // Откатываем изменения в случае ошибки
      loadPosts();
    }
  };

  const handleDeletePost = async (postId: number) => {
    try {
      await wallPostsApi.deletePost(postId);
      setPosts(posts.filter(post => post.id !== postId));
    } catch (error) {
      console.error('Ошибка при удалении поста:', error);
    }
  };

  const handleCreatePost = async (content: string, images?: File[], siteId?: number | null) => {
    if (!user?.id) {
      return;
    }

    try {
      const newPost = await wallPostsApi.createPost({
        content,
        siteId,
        images: images || [],
      });

      const postWithUserData: WallPost = {
        ...newPost,
        userName: user?.name || 'User',
        userAvatar: avatar || undefined
      };

      closeCreatePost();
      setPosts(prevPosts => [postWithUserData, ...prevPosts]);
    } catch (error) {
      console.error('Ошибка при создании поста:', error);
      throw error; // Пробрасываем ошибку для обработки в CreatePost
    }
  };

  return (
    <div className={styles.fullPageSocial}>
      {/* Основной контент */}
      <main className={styles.socialMain}>
        <div className={styles.instagramFeed}>
          {/* Посты */}
          <div className={styles.postsContainer}>
            {isLoadingPosts ? (
              // Показываем skeleton посты во время загрузки
              <>
                <SkeletonPost />
                <SkeletonPost />
                <SkeletonPost />
              </>
            ) : sortedPosts.length === 0 ? (
              <div className={styles.emptyPosts}>
                <i className="ri-article-line"></i>
                <p>Пока нет постов</p>
                <span>Посты будут отображаться здесь</span>
              </div>
            ) : (
              sortedPosts.map(post => (
                <Post
                  key={post.id}
                  post={post}
                  onLike={handleLike}
                  onDelete={handleDeletePost}
                  onEdit={p => setEditingPost(p)}
                  currentUserId={user?.id || 0}
                  onUserClick={handleUserClick}
                  onSiteClick={onSiteClick}
                />
              ))
            )}
          </div>
        </div>
      </main>
      <BottomSheet
        isOpen={showCreatePost}
        onClose={closeCreatePost}
        title="Создать новый пост"
        size="large"
      >
        <CreatePost
          onCreatePost={async (content, images, siteId) => {
            try {
              await handleCreatePost(content, images, siteId);
            } catch (error) {
              // Ошибка уже залогирована в handleCreatePost
            }
          }}
          onCancel={closeCreatePost}
        />
      </BottomSheet>
      <FavoritesGridOverlay
        isOpen={showFavorites}
        posts={favoritePosts}
        onClose={closeFavorites}
        onPostClick={handleFavoriteGridClick}
      />
      {showFavoriteFeed && favoriteFeedInitialPostId !== null && (
        <PostsFeed
          posts={favoritePosts}
          initialPostId={favoriteFeedInitialPostId}
          onClose={() => {
            setShowFavoriteFeed(false);
            setFavoriteFeedInitialPostId(null);
          }}
          onLike={handleLike}
          onDelete={handleDeletePost}
          currentUserId={user?.id || 0}
          onUserClick={handleUserClick}
          onSiteClick={onSiteClick}
          loadPostsUserId={null} // Загружаем все посты (не только пользователя)
        />
      )}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onUpdated={updated => {
            setPosts(prev => prev.map(p => p.id === updated.id ? updated : p));
            setEditingPost(null);
          }}
        />
      )}
    </div>
  );
};

export default RyaskovProCard;
